import { Link } from "react-router";
import { ExternalLinkIcon, HourglassIcon, TriangleAlertIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Etiqueta } from "@/components/ui/etiqueta";
import { RUTA_DEL_MAESTRO } from "@/components/generacion/ArbolDeSecciones";
import { RevisionDeLaSeccion } from "@/components/generacion/RevisionDeLaSeccion";
import { useRedactarFinales, useRegenerarSeccion } from "@/hooks/useRedaccion";
import { getApiErrorMessage } from "@/lib/api";
import {
  cuentaDeLasFinales,
  DESCRIPCION_DEL_MODO,
  ETIQUETA_DE_MOTIVO_DE_LAS_FINALES,
  ETIQUETA_ESTADO_DE_REDACCION,
  metaDeLaRedaccion,
  redaccionCerrada,
  tonoDeLaSeccion,
  type DatosDeSecciones,
  type NumeroDeSeccion,
  type SeccionDelInforme,
} from "@/lib/redaccion";

interface PanelDeSeccionProps {
  informeId: number;
  seccion: SeccionDelInforme;
  /** La numeración del documento («3.1») y el capítulo al que pertenece. */
  numero: NumeroDeSeccion | null;
  datos: DatosDeSecciones;
  /** La siguiente sección de IA por revisar, si queda alguna. */
  siguiente: SeccionDelInforme | null;
  onIr: (itemId: number) => void;
  onEdicionSucia: (sucia: boolean) => void;
}

/**
 * El panel de la derecha: lo que corresponde a la sección elegida según cómo
 * se produce y en qué estado está.
 */
export function PanelDeSeccion({ informeId, seccion, numero, datos, siguiente, onIr, onEdicionSucia }: PanelDeSeccionProps) {
  const r = seccion.redaccion;
  const cerrada = redaccionCerrada(datos);

  return (
    <Card variant="marcada" className="min-w-0 gap-3.5 rounded-[1.25rem] px-6 py-5">
      <EncabezadoDelPanel seccion={seccion} numero={numero} />

      {!seccion.modo_confirmado ? (
        <ModoSinConfirmar />
      ) : !seccion.se_redacta_con_ia || r === null ? (
        <SeccionSinIa informeId={informeId} seccion={seccion} />
      ) : r.estado === "generando" ? (
        <Generando />
      ) : r.estado === "error" ? (
        <ConError informeId={informeId} seccion={seccion} cerrada={cerrada} />
      ) : r.estado === "pendiente" ? (
        seccion.es_final ? (
          <SeRedactaAlFinal informeId={informeId} datos={datos} />
        ) : (
          <SinRedactar informeId={informeId} seccion={seccion} cerrada={cerrada} />
        )
      ) : (
        <RevisionDeLaSeccion
          informeId={informeId}
          seccion={seccion}
          redaccion={r}
          cerrada={cerrada}
          siguiente={siguiente}
          onIr={onIr}
          onEdicionSucia={onEdicionSucia}
        />
      )}
    </Card>
  );
}

function EncabezadoDelPanel({ seccion, numero }: { seccion: SeccionDelInforme; numero: NumeroDeSeccion | null }) {
  const r = seccion.redaccion;
  const camino = [seccion.grupo, seccion.subgrupo].filter((x): x is string => Boolean(x)).join(" › ");
  const eyebrow = numero?.capitulo ? `${numero.capitulo}${numero.id ? ` · ${numero.id}` : ""}` : camino;
  const desactualizada = r?.estado === "validada" && r.desactualizada;

  return (
    <header className="flex flex-col gap-1">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          {eyebrow ? <p className="rotulo-seccion">{eyebrow}</p> : null}
          <h2 className="text-ink mt-1 text-[1.1875rem] leading-tight font-extrabold tracking-[-0.015em]">
            {seccion.nombre}
          </h2>
        </div>
        {seccion.se_redacta_con_ia && r ? (
          <Etiqueta tono={tonoDeLaSeccion(seccion)} className="mt-0.5 text-[0.6875rem]">
            {desactualizada ? "Desactualizada" : ETIQUETA_ESTADO_DE_REDACCION[r.estado]}
          </Etiqueta>
        ) : null}
      </div>
      {seccion.se_redacta_con_ia && r && r.texto ? (
        <p className="text-ink-muted text-xs font-semibold">{metaDeLaRedaccion(r)}</p>
      ) : null}
      {seccion.descripcion ? (
        <p className="text-ink-muted max-w-prose text-xs font-semibold text-pretty">{seccion.descripcion}</p>
      ) : null}
    </header>
  );
}

function ModoSinConfirmar() {
  return (
    <div className="bg-superficie-tenue flex flex-col items-start gap-3 rounded-[0.875rem] border-[1.5px] border-dashed p-4">
      <p className="text-ink-secundario text-[0.8125rem] font-semibold text-pretty">
        Este ítem todavía no tiene un modo de producción confirmado, así que no se redacta. Confirma en el maestro cómo se
        produce y vuelve.
      </p>
      <Button asChild size="sm" variant="outline" className="border-input h-auto rounded-[0.625rem] px-3 py-1.5 shadow-none">
        <Link to={RUTA_DEL_MAESTRO}>Ir al maestro de ítems</Link>
      </Button>
    </div>
  );
}

function SeccionSinIa({ informeId, seccion }: { informeId: number; seccion: SeccionDelInforme }) {
  const modo = seccion.modo && seccion.modo !== "redactado_ia" ? seccion.modo : null;
  const prosa = seccion.origen?.texto ?? null;
  // La prosa validada vive en una pestaña del expediente: ahí se corrige.
  const destino = seccion.origen ? `/informes/${informeId}/${seccion.origen.seccion}` : `/informes/${informeId}`;

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-superficie-tenue flex flex-col items-start gap-3 rounded-[0.875rem] border-[1.5px] p-4">
        <p className="text-ink text-[0.8125rem]">
          <strong>Cómo se produce: {seccion.modo_etiqueta ?? "—"}</strong>
        </p>
        {modo ? <p className="text-ink-secundario max-w-prose text-[0.8125rem] font-semibold text-pretty">{DESCRIPCION_DEL_MODO[modo]}</p> : null}
        <Button asChild size="sm" variant="outline">
          <Link to={destino}>
            Abrir en el expediente
            <ExternalLinkIcon aria-hidden />
          </Link>
        </Button>
      </div>

      {prosa ? (
        <div className="flex flex-col gap-1.5">
          <p className="text-ink-muted text-xs font-semibold">
            {seccion.origen?.validada ? "Prosa validada en el expediente" : "Prosa del expediente, todavía sin validar"}
          </p>
          <p className="text-ink-body max-w-3xl text-sm leading-[1.7] text-pretty whitespace-pre-line">{prosa}</p>
        </div>
      ) : null}
    </div>
  );
}

function Generando() {
  return (
    <div className="flex flex-col gap-2.5" aria-live="polite">
      <p className="text-ink-secundario flex items-center gap-2 text-[0.8125rem] font-semibold">
        <span aria-hidden className="bg-primary size-[0.4375rem] shrink-0 animate-pulse rounded-full" />
        La IA está escribiendo esta sección…
      </p>
      {["w-[96%]", "w-[88%]", "w-[92%]", "w-[60%]"].map((ancho) => (
        <span key={ancho} aria-hidden className={`bg-divisor block h-3 max-w-3xl animate-pulse rounded-md ${ancho}`} />
      ))}
    </div>
  );
}

function ConError({ informeId, seccion, cerrada }: { informeId: number; seccion: SeccionDelInforme; cerrada: boolean }) {
  const regenerar = useRegenerarSeccion(informeId);

  return (
    <div role="alert" className="bg-danger-soft flex flex-wrap items-center gap-3 rounded-xl px-4 py-3">
      <TriangleAlertIcon aria-hidden className="text-danger-texto size-4 shrink-0" />
      <div className="text-danger-texto-fuerte min-w-0 flex-1 text-[0.8125rem] font-semibold">
        <p className="font-bold">No se pudo redactar esta sección</p>
        <p>{seccion.redaccion?.error?.mensaje ?? "La redacción falló. Vuelve a intentarlo."}</p>
        {regenerar.isError ? <p>{getApiErrorMessage(regenerar.error, "No pudimos reintentar. Inténtalo de nuevo.")}</p> : null}
      </div>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="h-auto rounded-[0.625rem] px-3 py-1.5"
        disabled={cerrada || regenerar.isPending}
        onClick={() => regenerar.mutate({ itemId: seccion.item_id, instruccion: null })}
      >
        Reintentar
      </Button>
    </div>
  );
}

function SinRedactar({ informeId, seccion, cerrada }: { informeId: number; seccion: SeccionDelInforme; cerrada: boolean }) {
  const regenerar = useRegenerarSeccion(informeId);

  return (
    <div className="bg-superficie-tenue flex flex-col items-start gap-3 rounded-[0.875rem] border-[1.5px] border-dashed p-4">
      <p className="text-ink-secundario max-w-prose text-[0.8125rem] font-semibold text-pretty">
        Esta sección todavía no se redacta. La IA la escribe citando la hoja de hechos; después la revisas y la validas.
      </p>
      {regenerar.isError ? (
        <p className="text-danger-texto text-xs font-semibold">
          {getApiErrorMessage(regenerar.error, "No pudimos iniciar la redacción. Inténtalo de nuevo.")}
        </p>
      ) : null}
      <Button
        type="button"
        size="sm"
        variant="fuerte"
        className="h-auto rounded-[0.625rem] px-3 py-1.5"
        disabled={cerrada || regenerar.isPending}
        onClick={() => regenerar.mutate({ itemId: seccion.item_id, instruccion: null })}
      >
        Redactar esta sección
      </Button>
    </div>
  );
}

/**
 * El resumen ejecutivo y las conclusiones leen todo lo demás, así que se
 * redactan al final: el botón espera a que el resto esté validado y al día, y
 * mientras tanto dice qué falta.
 */
function SeRedactaAlFinal({ informeId, datos }: { informeId: number; datos: DatosDeSecciones }) {
  const finales = useRedactarFinales(informeId);
  const { listas, total } = cuentaDeLasFinales(datos);
  const { finales_habilitadas: habilitada, finales_faltan: faltan } = datos.resumen;

  return (
    <div className="bg-superficie-tenue flex flex-col gap-3 rounded-[0.875rem] border-[1.5px] border-dashed p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-60 flex-1">
          <p className="text-ink text-[0.8125rem] font-bold">Se redacta al final</p>
          <p className="text-ink-secundario mt-0.5 text-xs font-semibold text-pretty">
            Lee todas las demás secciones. <span className="font-mono">{listas} de {total} listas</span>
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="fuerte"
          className="h-auto rounded-[0.625rem] px-3 py-1.5"
          disabled={redaccionCerrada(datos) || !habilitada || finales.isPending}
          onClick={() => finales.mutate()}
        >
          <HourglassIcon aria-hidden />
          Redactar ahora
        </Button>
      </div>

      {faltan.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {faltan.map((f) => (
            <li key={f.item_id} className="text-ink-secundario text-xs font-semibold">
              <strong className="text-ink">{f.nombre}</strong> · {ETIQUETA_DE_MOTIVO_DE_LAS_FINALES[f.motivo]}
            </li>
          ))}
        </ul>
      ) : null}

      {finales.isError ? (
        <p className="text-danger-texto text-xs font-semibold">
          {getApiErrorMessage(finales.error, "No pudimos iniciar la redacción. Inténtalo de nuevo.")}
        </p>
      ) : null}
    </div>
  );
}
